<template>
	<div>
		<BaseHeader :title="$t('company_ads').toString()"> </BaseHeader>
		<div class="main" style="min-height: 850px">
			<form v-if="!isLoading">
				<div class="w-full flex grid grid-cols-2 justify-around">
					<div class="flex flex-col justify-start pl-5">
						<div class="flex mb-6">
							<div class="font-bold mr-5" style="color: #495057">
								{{ $t('preview') }}
							</div>
							<div class="flex rounded-full h-6 w-6 justify-center cursor-pointer tooltip" style="background-color: lightgrey; align-items: center">
								<font-awesome-icon :icon="['fas', 'info']" class="flex justify-center text-connectProBlue" style="align-content: center" />
								<div class="tooltiptext mt-24 ml-5 z-10">
									{{ $t('ad_formatting_hint') }}
								</div>
							</div>
						</div>
						<SmartphonePreviewComponent :background-image="backgroundImage" :special-offer="specialOffer" :video-content="videoContent" />
					</div>
					<div class="flex flex-col w-full border-l-2 border-connectProBlue pl-5">
						<div class="flex flex-col w-full pr-0 mb-6">
							<div class="flex font-bold mb-4">{{ $t('recipient_filter') }}</div>
							<div class="w-1/3 mb-4">
								<BaseButton
									@click="
										$store.commit('setConfirmLeaving', false);
										$router.push({ name: 'repair_shop.company_ads.ad_filters', params: { id: specialOffer.id, type: $route.params?.type } });
									"
									class="mr-4 w-full"
									>{{ $t('search_customer_data') }}</BaseButton
								>
							</div>
							<div v-if="selectedFilters.length > 0">
								<div class="flex grid-cols-2 gap-16 text-left text-red-300 font-bold" v-if="!isUserAllowedInLevel1">
									<div class="w-1/4">
										{{ $t('affected_customers_input') }}
									</div>
									<div>
										{{ affectedCustomers }}
									</div>
								</div>
								<div class="flex grid-cols-2 gap-16 text-left">
									<div class="font-bold w-1/4">
										{{ $t('chosen_filters_input') }}
									</div>
									<div class="flex flex-col">
										<div class="" v-for="filter in formatSelectedFilters" :key="filter.key">
											{{ filterNames.find(element => element.value === filter.key)?.display }}: {{ filter.value }}
										</div>
									</div>
								</div>
							</div>
						</div>
						<div v-if="!isUserAllowedInLevel1">
							<div class="flex flex-col w-1/2 pr-0 md:pr-4 border-t-2 border-gray-200 pt-2">
								<BaseDatepicker
									ref="BaseInput.value.adBegin"
									:required="true"
									:label="$t('start')"
									placeholder="01.01.2021"
									error-behavior="blur"
									v-model="specialOffer.start"
									class="md:mr-4 w-full"
								/>
							</div>
							<div class="flex flex-col w-1/2 pr-0 md:pr-4">
								<BaseDatepicker
									ref="BaseInput.value.adEnd"
									:required="true"
									:label="$t('end')"
									placeholder="01.01.2021"
									error-behavior="blur"
									v-model="specialOffer.end"
									class="md:mr-4 w-full"
									:min_date="specialOffer.start"
								/>
							</div>
						</div>

						<div class="w-full pr-0">
							<BaseInput ref="BaseInput.specialoffer.title" label="Titel" :required="true" type="text" v-model="specialOffer.headline" />
						</div>

						<div class="w-full flex mt-2 gap-4">
							<BaseButton @click="openUpload" style="width: 200px">
								<input type="file" ref="file" accept="image/*" @change="chosenUpload" class="hidden" />
								<font-awesome-icon :icon="['fas', 'image']" class="w-5 h-5 pr-1" size="lg" />
								{{ $t('upload_image_file') }}
							</BaseButton>
							<BaseButton @click="openVideoUpload" style="width: 200px">
								<input type="file" ref="videoFile" accept="video/*" @change="chosenVideoUpload" name="movie" id="movie" class="hidden" />
								<font-awesome-icon :icon="['fas', 'video']" class="w-5 h-5 pr-1" size="lg" />
								{{ $t('upload_video_file') }}
							</BaseButton>
						</div>
						<div class="w-full flex mt-2 mb-6">
							<div v-if="backgroundImage" class="flex">
								{{ fileName }}
								<div class="ml-4 cursor-pointer">
									<font-awesome-icon icon="trash" @click="clearAdsImage" />
								</div>
							</div>
							<div v-if="videoContent" class="flex">
								{{ fileName }}
								<div class="ml-4 cursor-pointer">
									<font-awesome-icon icon="trash" @click="clearAdsVideo" />
								</div>
							</div>
						</div>
						<BaseInput v-model="specialOffer.content" :label="$t('content')" ref="BaseInput.content" :required="true">
							<textarea
								ref="BaseInput.specialoffer.content"
								style="word-break: break-word"
								class="resize-y w-full shadow-m h-32"
								:placeholder="$t('ad_content_hint')"
								v-model="specialOffer.content"
								:required="true"
							></textarea>
						</BaseInput>
					</div>
				</div>
				<router-view />
			</form>
			<LoadingSpinner v-else />
		</div>
		<Footer @backButtonClick="goBack" @continueButtonClick="save" :is-back-button="true" :is-continue-button="true" :is-skippable="false" class="m-8"
			><template #continueButton>
				<BaseButton @click="save" :disabled="isLoading" class="mr-4 btn-blue">{{ $t('general.save') }}</BaseButton>
			</template></Footer
		>
	</div>
</template>

<script>
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker.vue';
import Validate from '@/mixins/Validate';
import { getGermanDate } from '@/plugins/Utils';
import { AdType } from '@/helpers/enums';
import regex from '@/helpers/Regex';
import Authentication from '@/mixins/Authentication';
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import Footer from '@/components/damagecases/Footer.vue';
import SmartphonePreviewComponent from '@/views/management/RepairShop/Ads/SmartphonePreviewComponent.vue';
import router from '@/router';
import SpecialOfferRepository from '@/api/repositories/SpecialOfferRepository';
import RouteWatcher from '@/mixins/RouteWatcher';
import SpecialOfferTemplateRepository from '@/api/repositories/SpecialOfferTemplateRepository';

export default {
	name: 'CreateAd',
	computed: {
		AdType() {
			return AdType;
		},
		regex() {
			return regex;
		},
		formatSelectedFilters() {
			const grouped = this.selectedFilters.reduce((acc, item) => {
				const key = item.special_offer_filter.key;
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(item);
				return acc;
			}, {});

			return Object.entries(grouped).map(([key, elements]) => ({
				key,
				value: elements.map(element => element.link_data.value).join(', '),
			}));
		},
	},
	components: {
		SmartphonePreviewComponent,
		BaseHeader,
		BaseDatepicker,
		LoadingSpinner,
		BaseInput,
		BaseButton,
		Footer,
	},
	mixins: [Validate, Authentication, RouteWatcher],
	data() {
		return {
			specialOffer: { media: {} },
			backgroundImage: null,
			videoContent: null,
			isLoaded: false,
			filterOptions: [],
			selectedFilters: [],
			isLoading: false,
			affectedCustomers: 0,
			isShowingFilePicker: false,
			fileExtension: '',
			fileName: '',
			filterNames: [],
		};
	},
	methods: {
		router() {
			return router;
		},
		getGermanDate,
		goBack() {
			this.$router.go(-1);
		},
		clearAdsImage() {
			this.backgroundImage = null;
			this.fileExtension = '';
			this.fileName = '';
		},
		clearAdsVideo() {
			this.videoContent = null;
			this.fileExtension = '';
			this.fileName = '';
		},
		openUpload() {
			if (this.isShowingFilePicker === false) {
				if (this.videoContent) {
					this.$swal({
						title: this.$t('general.attention').toString(),
						text: this.$t('video_uploaded_overwrite').toString(),
						cancelButtonText: this.$t('general.cancel').toString(),
						showCancelButton: true,
						confirmButtonText: this.$t('general.yes').toString(),
						icon: 'warning',
					}).then(result => {
						if (result.value) {
							this.isShowingFilePicker = true;
							this.$refs.file.click();
						}
					});
				} else {
					this.isShowingFilePicker = true;
					this.$refs.file.click();
				}
			}
		},
		openVideoUpload() {
			if (this.isShowingFilePicker === false) {
				if (this.backgroundImage) {
					this.$swal({
						title: this.$t('general.attention').toString(),
						text: this.$t('image_uploaded_overwrite').toString(),
						cancelButtonText: this.$t('general.cancel').toString(),
						showCancelButton: true,
						confirmButtonText: this.$t('general.yes').toString(),
						icon: 'warning',
					}).then(result => {
						if (result.value) {
							this.isShowingFilePicker = true;
							this.$refs.videoFile.click();
						}
					});
				} else {
					this.isShowingFilePicker = true;
					this.$refs.videoFile.click();
				}
			}
		},
		chosenUpload() {
			this.isShowingFilePicker = false;
			const file = this.$refs.file.files[0];
			const reader = new FileReader();

			if (!file.type.startsWith('image/')) {
				return false;
			}

			this.fileName = file.name;
			this.fileExtension = file.name
				.split('.')
				.pop()
				.toLowerCase();

			this.videoContent = null;
			reader.onload = e => {
				this.backgroundImage = e.target.result;
			};

			reader.readAsDataURL(file);
		},
		chosenVideoUpload() {
			this.isShowingFilePicker = false;
			const file = this.$refs.videoFile.files[0];
			if (!file) return;

			this.fileName = file.name;
			this.fileExtension = file.name
				.split('.')
				.pop()
				.toLowerCase();

			const reader = new FileReader();
			reader.readAsDataURL(file);

			this.backgroundImage = null;
			reader.onload = () => {
				this.videoContent = reader.result;
			};

			reader.onerror = () => {
				console.error('Fehler beim Laden der Datei');
			};
		},
		addHours(date) {
			const cloneD = new Date();
			cloneD.setTime(date.getTime());

			const minutesInMillis = 240 * 60 * 1000;
			cloneD.setTime(cloneD.getTime() + minutesInMillis);
			return cloneD;
		},
		getAdByID() {
			if (this.isUserAllowedInLevel1 || this.$route.params.type === 'from_template') {
				new SpecialOfferTemplateRepository().getTemplateById(this.specialOffer.id).then(response => {
					if (response.data.data != null) {
						this.setAdValuesFromApi(response.data.data);
					}
					//	this.isLoading = false;
				});
			} else if (this.isUserAllowedInLevel5or6) {
				new SpecialOfferRepository().getOfferById(this.specialOffer.id).then(response => {
					if (response.data.data != null) {
						this.setAdValuesFromApi(response.data.data);
					}
					//	this.isLoading = false;
				});
			}
		},
		setAdValuesFromApi(marketingAd) {
			this.specialOffer = marketingAd;

			if (marketingAd.start && marketingAd.end) {
				this.specialOffer.start = new Date(marketingAd.start);
				this.specialOffer.end = new Date(marketingAd.end);
			}

			if (marketingAd.media) {
				if (marketingAd.media.type === 'image') {
					this.backgroundImage = marketingAd.media.base64content;
				} else if (marketingAd.media.type === 'video') {
					this.videoContent = marketingAd.media.base64content;
				}
				this.fileName = 'datei.' + marketingAd.media.extension;
				this.fileExtension = marketingAd.media.extension;
			} else {
				this.specialOffer.media = {};
			}

			if (marketingAd.filters) {
				marketingAd.filters.forEach(filter => {
					if (filter.special_offer_filter.operator === '>=') {
						filter.internId = 1;
					} else if (filter.special_offer_filter.operator === '<=') {
						filter.internId = 2;
					} else {
						filter.internId = 1;
					}
				});
				this.$store.commit('setMarketingFilters', {
					filters: marketingAd.filters,
					affectedCustomers: marketingAd.previewFilters?.total_count,
					filterResponse: marketingAd.previewFilters?.individual_counts,
				});

				this.selectedFilters = this.$store.getters.getMarketingFilters;
				this.affectedCustomers = this.$store.getters.getAffectedCustomers;
			}
			this.isLoading = false;
		},
		save() {
			if (!this.validate()) {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
				return;
			}
			if (this.backgroundImage == null && (this.specialOffer.content === '' || this.specialOffer.content === undefined)) {
				this.$swal(this.$t('general.attention').toString(), this.$t('upload_image_or_set_content').toString(), 'warning');
				return;
			}
			this.isLoading = true;
			if (this.backgroundImage != null) {
				this.specialOffer.media.type = 'image';
				this.specialOffer.media.extension = this.fileExtension;
				this.specialOffer.media.base64content = this.backgroundImage;
			}
			if (this.videoContent != null) {
				this.specialOffer.media.type = 'video';
				this.specialOffer.media.extension = this.fileExtension;
				this.specialOffer.media.base64content = this.videoContent;
			}

			if (!this.isUserAllowedInLevel1) {
				this.specialOffer.start = this.addHours(this.specialOffer.start);
				this.specialOffer.end = this.addHours(this.specialOffer.end);
			}

			if (this.$store.getters.getMarketingFilters.length > 0) {
				this.specialOffer.filters = this.$store.getters.getMarketingFilters;
				// this.specialOffer.filters[0].special_offer_filter.id = 2;
			}

			this.$store.commit('clearMarketingAds');
			if (this.isUserAllowedInLevel1) {
				if (this.specialOffer.id != null) {
					new SpecialOfferTemplateRepository()
						.updateTemplate(this.specialOffer.id, this.specialOffer)
						.then(response => {
							this.isLoading = false;
							this.$swal(this.$t('general.success').toString(), this.$t('marketing_template_saved_successfully').toString(), 'success').then(() => {
								this.$store.commit('setConfirmLeaving', false);
								this.$router.replace({ name: 'repair_shop.ad_templates' });
							});
						})
						.catch(error => {
							console.log(error);
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('marketing_template_publish_error').toString(), 'error');
						});
				} else {
					new SpecialOfferTemplateRepository()
						.storeTemplate(this.specialOffer)
						.then(response => {
							this.isLoading = false;
							this.$store.commit('clearMarketingAds');
							this.$swal(this.$t('general.success').toString(), this.$t('marketing_template_saved_successfully').toString(), 'success').then(() => {
								this.$store.commit('setConfirmLeaving', false);
								this.$router.replace({ name: 'repair_shop.ad_templates' });
							});
						})
						.catch(e => {
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('marketing_template_publish_error').toString(), 'error');
						});
				}
			} else {
				if (this.specialOffer.id != null && !this.$route.params?.type && this.$route.params?.type !== 'from_template') {
					new SpecialOfferRepository()
						.updateOffer(this.specialOffer.id, this.specialOffer)
						.then(response => {
							this.isLoading = false;
							this.$swal(this.$t('general.success').toString(), this.$t('ad_published_successfully').toString(), 'success').then(() => {
								this.$store.commit('setConfirmLeaving', false);
								this.$router.replace({ name: 'repair_shop.company_ads' });
							});
						})
						.catch(error => {
							console.log(error);
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('ad_publish_error').toString(), 'error');
						});
				} else {
					if (this.$route.params?.type === 'from_template') {
						this.specialOffer.id = null;
					}
					new SpecialOfferRepository()
						.storeOffer(this.specialOffer)
						.then(response => {
							this.isLoading = false;
							this.$store.commit('clearMarketingAds');
							this.$swal(this.$t('general.success').toString(), this.$t('ad_published_successfully').toString(), 'success').then(() => {
								this.$store.commit('setConfirmLeaving', false);
								this.$router.replace({ name: 'repair_shop.company_ads' });
							});
						})
						.catch(error => {
							console.log(error);
							this.isLoading = false;
							this.$swal(this.$t('general.error').toString(), this.$t('ad_publish_error').toString(), 'error');
						});
				}
			}
		},
		getFilters() {
			new SpecialOfferRepository()
				.getFilters()
				.then(response => {
					const whiteLabels = ['vehicle_registration_first', 'vehicle_hu', 'vehicle_kilometrage', 'vehicle_kw', 'vehicle_displacement'];
					Object.values(response.data).forEach(values => {
						if (whiteLabels.includes(values[0].key)) {
							const name = values[0].label.split(' ')[0];
							this.filterNames.push({ display: name, value: values[0].key });
						} else {
							this.filterNames.push({ display: values[0].label, value: values[0].key });
						}
					});
				})
				.catch(e => {
					console.log(e);
				});
		},
		onRouteChange() {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			this.selectedFilters = this.$store.getters.getMarketingFilters;
			this.affectedCustomers = this.$store.getters.getAffectedCustomers;
		},
	},
	mounted() {
		if (!(this.isUserAllowedInLevel5or6 && this.isAdAdmin) && !this.isUserAllowedInLevel1) {
			this.$emit('authNotAllowed');
		} else {
			this.$store.commit('clearMarketingAds');
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			this.getFilters();
			this.$refs.file.addEventListener('cancel', () => {
				this.isShowingFilePicker = false;
			});
			this.$refs.videoFile.addEventListener('cancel', () => {
				this.isShowingFilePicker = false;
			});

			if (this.$route.params.id != null) {
				this.isLoading = true;
				this.specialOffer.id = this.$route.params.id;
				this.getAdByID();
			}
		}
	},
	watch: {
		'specialOffer.image': function() {
			this.backgroundImage = this.specialOffer.image;
		},
	},
};
</script>

<style scoped></style>
